import React, { FC } from 'react';
import { TypeBadge, TypeImage } from '@/core/generated-types';
import NextImage from 'next/image';
import { Asset } from 'contentful';
import classNames from 'classnames';
import { Badge } from '@/components';

interface ImageProps {
  imageData?: TypeImage | undefined;
  className?: string;
  width?: number;
  height?: number;
  alt?: string;
  altText?: string;
  Image?: Asset;
  entryName?: string;
  loading?: 'lazy' | 'eager';
  testId?: string;
  quality?: number;
  style?: any;
}

const _formatImageUrl = (url: string | undefined) => {
  if (!url) {
    return 'https://placehold.co/600x400/png';
  }

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  // URL starts with http, https, or is relative
  return url;
};

const ImageContainer = ({
  pillBadge,
  children,
}: {
  pillBadge?: TypeBadge;
  children: React.ReactNode;
}) => {
  if (!pillBadge) return children;

  return (
    <div className="relative">
      <Badge {...pillBadge.fields} classes="absolute top-4 left-3 md:left-4" />
      {children}
    </div>
  );
};

const LuvImage: FC<ImageProps> = ({
  imageData,
  entryName,
  Image,
  altText,
  loading = 'lazy',
  className = 'mx-auto',
  width = Image?.fields?.file?.details?.image?.width ?? 200,
  height = Image?.fields?.file?.details?.image?.height ?? 200,
  testId,
  quality = 75,
  style,
}) => {
  const imageObj = imageData ? imageData.fields : { entryName, Image, altText };

  if (!imageObj) {
    return null;
  }

  return (
    <ImageContainer pillBadge={imageData?.fields.pillBadge}>
      <NextImage
        data-testid={testId || `image_${imageObj.entryName}`}
        alt={imageObj?.altText || ''}
        src={_formatImageUrl(imageObj.Image?.fields?.file?.url)}
        className={classNames(className)}
        width={width}
        height={height}
        loading={loading}
        quality={quality}
        style={style}
      />
    </ImageContainer>
  );
};

export default React.memo(LuvImage);
